import React, { useEffect, useRef, useState, useCallback } from 'react';
import './venture.css';
import { emptyReg, blankCo, STEPS, LOCKED_OFFICE } from './types';
import type { Project, RegState, View, Tab, Co } from './types';
import * as api from './api';
import VentureList from './VentureList';
import VentureRegister from './VentureRegister';
import VentureDetail from './VentureDetail';
import LpPool from './LpPool';
import type { Lp, LpInput } from './api';
import { lpIntoProject } from './lpMove';

interface Props {
  me: unknown;
  onBack: () => void;
}

function todayISO(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}

const TABS: [Tab, string][] = [
  ['progress', '📊 진행현황'],
  ['reg', '📝 등록정보'],
  ['collect', '📥 자료수집'],
  ['master', '🗂 마스터정보'],
  ['docs', '📄 서류'],
];

export default function Venture({ onBack }: Props) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [view, setView] = useState<View>('list');
  const [pid, setPid] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('progress');
  const [reg, setReg] = useState<RegState>(emptyReg());
  const [toast, setToast] = useState('');
  const saveTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await api.listProjects();
      const full: Project[] = [];
      for (const s of rows) {
        try {
          const row = await api.getProject(s.id);
          full.push(normalize(row.data, row.id, row.org, row.step));
        } catch {
          full.push(normalize(undefined, s.id, s.org, s.step));
        }
      }
      setProjects(full);
    } catch (e) {
      setToast((e as Error).message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // LP 후보 메모 (2026-10-08) — 목록의 「LP n」 칩 · 매칭 조합 고르기에 프로젝트 목록을 같이 쓴다
  const [lps, setLps] = useState<Lp[]>([]);
  const [lpLoaded, setLpLoaded] = useState(false);
  const [lpErr, setLpErr] = useState('');
  const loadLp = useCallback(() => {
    setLpLoaded(false);
    api.listLp().then((r) => { setLps(r); setLpErr(''); setLpLoaded(true); })
      .catch((e) => { setLpErr((e as Error).message || '불러오기 실패'); setLpLoaded(true); });   // 실패를 「없음」으로 보이지 않게
  }, []);
  useEffect(() => { loadLp(); }, [loadLp]);
  const lpByProject: Record<string, string[]> = {};
  lps.forEach((l) => { if (l.project_id) (lpByProject[l.project_id] = lpByProject[l.project_id] || []).push(l.company); });
  async function saveLp(id: string | null, f: LpInput): Promise<boolean> {
    try {
      let row = id ? await api.updateLp(id, f) : await api.createLp(f);
      setLps((arr) => (id ? arr.map((x) => (x.id === id ? row : x)) : [row, ...arr]));
      let msg = id ? 'LP 후보 저장됨' : 'LP 후보 추가됨';
      // 조합에 매칭되면 그 조합으로 옮긴다 — 등록정보 회사(소개 팀장님) · 자료수집 LP 카드(미리 받은 링크 그대로) · 조합원 명부 (2026-10-12)
      if (row.project_id && row.moved_project_id !== row.project_id) {
        const target = projects.find((x) => x.id === row.project_id);
        if (target) {
          try {
            const pre = row.collect_pid ? await api.getLpPortalItems(row.id).then((x) => x.items).catch(() => undefined) : undefined;   // 「항목 편집」으로 고친 항목 그대로(2026-11-01)
            const np = lpIntoProject(target, row, pre);
            if (np) { await api.updateProject(np.id, np); setProjects((arr) => arr.map((x) => (x.id === np.id ? np : x))); }
            row = await api.updateLp(row.id, { moved_project_id: row.project_id });
            setLps((arr) => arr.map((x) => (x.id === row.id ? row : x)));
            msg = np ? `「${row.company}」을(를) ${target.org || '조합'} LP 로 옮겼어요${row.collect_pid ? ' · 받아 둔 자료 그대로' : ''}` : `${target.org || '조합'}에 이미 들어가 있어요`;
          } catch (e) { msg = 'LP 후보는 저장했지만 조합으로 옮기지 못했어요 — ' + ((e as Error).message || ''); }
        }
      }
      setToast(msg);
      return true;
    } catch (e) { setToast('⚠ ' + (e as Error).message); return false; }
  }
  const patchLpRow = (id: string, patch: Partial<Lp>) => setLps((arr) => arr.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  async function removeLp(id: string): Promise<boolean> {
    try { await api.deleteLp(id); setLps((arr) => arr.filter((x) => x.id !== id)); setToast('LP 후보 삭제됨'); return true; }
    catch (e) { setToast('⚠ ' + (e as Error).message); return false; }
  }

  // 그룹 지정 · 빼기 · 이름 바꾸기 (2026-10-08) — 서버는 data.group 한 칸만 고친다
  /** 별표 켜기 · 끄기 — 화면 먼저 바꾸고(바로 보이게) 실패하면 되돌림 (2026-10-19) */
  async function setStar(id: string, on: boolean): Promise<void> {
    const put = (v: boolean) => setProjects((arr) => arr.map((p) => { if (p.id !== id) return p; const n: Project = { ...p }; if (v) n.star = true; else delete n.star; return n; }));
    put(on);
    try { await api.setStar([id], on); } catch (e) { put(!on); setToast('⚠ ' + (e as Error).message); }
  }
  async function setGroup(ids: string[], group: string | null): Promise<boolean> {
    try {
      const r = await api.setGroup(ids, group);
      const g = r.group || '';
      setProjects((arr) => arr.map((p) => {
        if (!ids.includes(p.id)) return p;
        const n: Project = { ...p };
        if (g) n.group = g; else delete n.group;
        return n;
      }));
      setToast(g ? '「' + g + '」 그룹 · ' + r.updated + '건' : '그룹에서 뺐습니다 · ' + r.updated + '건');
      return true;
    } catch (e) { setToast('⚠ ' + (e as Error).message); return false; }
  }

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(''), 2100);
    return () => window.clearTimeout(t);
  }, [toast]);

  const cur = pid ? projects.find((p) => p.id === pid) || null : null;

  const [saving, setSaving] = useState(false);
  const [saveAt, setSaveAt] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const dirtyRef = useRef(false);          // 마지막 성공 저장 이후 바뀐 게 있는지
  const lastRef = useRef<Project | null>(null);

  // 실제 저장 — 실패는 숨기지 않는다. 예전엔 catch 에서 조용히 넘겨 로그인 만료 뒤에도
  // 「✓ 자동저장」만 보이며 입력이 계속됐고, 창을 닫으면 전부 사라졌다.
  const persist = useCallback((snap: Project) => {
    setSaving(true);
    api.updateProject(snap.id, snap)
      .then(() => {
        setSaving(false); setSaveErr('');
        if (lastRef.current === snap) dirtyRef.current = false;
        setSaveAt(new Date().toTimeString().slice(0, 8));
      })
      .catch((e) => {
        setSaving(false);
        const msg = (e as Error).message || '저장 실패';
        setSaveErr(msg);
        setToast('⚠ 자동저장 실패 — ' + msg);
      });
  }, []);

  useEffect(() => {
    if (!cur) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    const snap = cur;
    lastRef.current = snap;
    dirtyRef.current = true;
    setSaving(true);
    saveTimer.current = window.setTimeout(() => persist(snap), 600);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
  }, [cur, persist]);

  // 페이지 이탈 보호 — 600ms 디바운스 안에 닫거나, 저장 실패 상태로 닫으면 마지막 변경이 사라진다
  useEffect(() => {
    const onLeave = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current && !saveErr) return;
      if (lastRef.current && dirtyRef.current) persist(lastRef.current);   // 마지막 스냅샷을 즉시 시도
      e.preventDefault(); e.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [saveErr, persist]);
  // 창으로 돌아오면 보던 프로젝트를 다시 읽는다 — 업무관리에서 벤처 단계를 바꿨으면(_stepRev 가 큼) 그 진행으로 (2026-10-11 양방향)
  //  고친 뒤 아직 저장 전(dirty)이면 건드리지 않는다 · 서버도 옛 진행이 새 진행을 덮지 않게 지킨다(PUT)
  useEffect(() => {
    if (!pid) return undefined;
    const onFocus = () => {
      if (dirtyRef.current) return;
      api.getProject(pid).then((row) => {
        const d = (row.data || {}) as Project & { _stepRev?: number };
        setProjects((arr) => arr.map((p) => {
          if (p.id !== pid) return p;
          const mine = Number((p as Project & { _stepRev?: number })._stepRev) || 0;
          return (Number(d._stepRev) || 0) > mine ? normalize(row.data, row.id, row.org, row.step) : p;
        }));
      }).catch(() => { /* 다시 읽기 실패 — 지금 화면 그대로 */ });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [pid]);
  const retrySave = () => { if (lastRef.current) persist(lastRef.current); };
  // 상세에서 고친 직후(600ms 안) 다른 메뉴 · 목록으로 가면 cur 가 null 이 되며 저장 타이머가 지워져 마지막 입력이 사라졌다 (2026-10-08 검수)
  //  → 보던 프로젝트가 바뀌는 순간, 저장 안 된 마지막 스냅샷이 있으면 바로 저장
  const prevPid = useRef<string | null>(pid);
  useEffect(() => {
    const was = prevPid.current;
    prevPid.current = pid;
    if (was && was !== pid && dirtyRef.current && lastRef.current && lastRef.current.id === was) persist(lastRef.current);
  }, [pid, persist]);

  function patchCur(patch: Partial<Project>) {
    if (!pid) return;
    setProjects((arr) => arr.map((p) => (p.id === pid ? { ...p, ...patch } : p)));
  }

  function goList() { setView('list'); setPid(null); }
  function openProj(id: string) { setPid(id); setView('detail'); setTab('progress'); }
  function openTab(k: Tab) { setView('detail'); setTab(k); }

  function regField(k: keyof RegState, v: string) { setReg((r) => ({ ...r, [k]: v })); }
  function regCo(i: number, f: keyof Co, v: string) {
    setReg((r) => { const cos = r.cos.slice(); cos[i] = { ...cos[i], [f]: v }; return { ...r, cos }; });
  }
  function patchCos(ps: Array<Partial<Co> | null>) { setReg((r) => ({ ...r, cos: r.cos.map((c, j) => (ps[j] ? { ...c, ...ps[j] } : c)) })); }
  function addCo() { setReg((r) => ({ ...r, cos: [...r.cos, blankCo()] })); }
  function removeCo(i: number) { setReg((r) => (r.cos.length > 1 ? { ...r, cos: r.cos.filter((_, j) => j !== i) } : r)); }
  function patchCo(i: number, patch: Partial<Co>) {
    setReg((r) => ({ ...r, cos: r.cos.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  }

  async function removeProject(id: string) {
    try {
      await api.deleteProject(id);
      setProjects((arr) => arr.filter((x) => x.id !== id));
      setLps((arr) => arr.map((l) => (l.project_id === id ? { ...l, project_id: null, status: l.status === 'matched' ? 'wait' : l.status } : l)));   // 서버도 같은 처리(매칭 → 대기 · SET NULL)
      if (pid === id) { setPid(null); setView('list'); }
      setToast('프로젝트 삭제됨');
    } catch (e) {
      setToast((e as Error).message);
    }
  }

  async function submitReg() {
    if (!reg.org.trim()) { setToast('조합명칭만 입력하면 등록됩니다'); return; }
    // 투자 처리 방법은 처음 등록 때 꼭(2026-10-16 지도사 「최초 등록을 해야 안 까먹어서」) — 회사 이름 · 번호를 적은 법인만
    if (!(reg.investPlan || '').trim()) { setToast('투자 처리 방법을 적어 주세요(모르면 「미정」)'); return; }   // 조합 단위(2026-10-19)
    const c0 = reg.cos[0];
    const local: Project = {
      id: 'tmp', org: reg.org, investPlan: reg.investPlan.trim(), withdraw: reg.withdraw || '', bond: reg.bond || '', office: reg.office || 'roum', addr: reg.addr, total: reg.total, per: reg.per, dur: reg.dur,
      gp: reg.gp, gpTel: reg.gpTel, gpEmail: reg.gpEmail, regDate: todayISO(),
      fp: c0.fpName || '', fpCo: c0.fpCo || '한화생명',
      cos: JSON.parse(JSON.stringify(reg.cos)) as Co[], step: 1,
    };
    try {
      const row = await api.createProject(local);
      local.cos.forEach((co) => { if (co.companyId) api.linkCompany(row.id, co.companyId).catch(() => undefined); });
      const saved: Project = { ...local, id: row.id };
      setProjects((arr) => [saved, ...arr]);
      setReg(emptyReg());
      setPid(row.id); setView('detail'); setTab('progress');
      setToast('프로젝트 등록 완료 · 업무관리·자료수집 연동');
    } catch (e) {
      setToast((e as Error).message);
    }
  }

  function advanceStep() { if (cur && cur.step < STEPS.length) patchCur({ step: cur.step + 1 }); }
  function backStep() { if (cur && cur.step > 1) patchCur({ step: cur.step - 1 }); }

  let title = '📋 벤처인증 프로젝트';
  if (view === 'register') title = '🚀 새 프로젝트 등록';
  else if (view === 'lp') title = '🤝 LP 후보';
  else if (view === 'detail' && cur) title = '🚀 ' + cur.org;

  return (
    <div className={'venture-root' + (LOCKED_OFFICE ? ' embed' : '')}>
      <div className="mwrap">
        {LOCKED_OFFICE ? null : <div className="mside">
          <div className="mbrand" onClick={LOCKED_OFFICE ? undefined : onBack}>
            <div className="b1">🚀 벤처인증</div>
            <div className="b2">벤처투자유형</div>
          </div>
          <div className="nav">
            <div className={'nitem ' + (view === 'list' || view === 'register' ? 'on' : '')} onClick={goList}>📋 <span>프로젝트 목록</span></div>
            <div className={'nitem ' + (view === 'lp' ? 'on' : '')} onClick={() => { setView('lp'); setPid(null); }}>🤝 <span>LP 후보</span>{lps.some((l) => l.status === 'wait') ? <span className="ncnt" title="대기 중인 LP 후보">{lps.filter((l) => l.status === 'wait').length}</span> : null}</div>
            {cur ? (
              <>
                <div className="nsec">{cur.org.length > 10 ? cur.org.slice(0, 10) + '…' : cur.org}</div>
                {TABS.map(([k, l]) => (
                  <div key={k} className={'nitem ' + (view === 'detail' && tab === k ? 'on' : '')} onClick={() => openTab(k)}>{l}</div>
                ))}
              </>
            ) : null}
          </div>
          <div className="foot"><button className="homebtn" onClick={onBack}>← 홈으로</button></div>
        </div>}
        <div className="mmain">
          {LOCKED_OFFICE ? (view === 'list' ? null : (
            // 올댓벤처스 어드민 안(iframe) — 왼쪽 메뉴 · 제목 띠 대신 목록으로 · 조합 탭만 위 한 줄 (2026-10-17 지도사 「이거만 나와야지」)
            <div className="etop">
              <button type="button" className="eback" onClick={goList}>← 목록</button>
              {view === 'lp' ? <span className="eorg">🤝 LP 후보</span> : view === 'register' ? <span className="eorg">새 프로젝트 등록</span> : null}
              {view === 'detail' && cur ? (
                <>
                  <span className="eorg" title={cur.org}>{cur.org}</span>
                  <div className="etabs" role="tablist">
                    {TABS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => openTab(k)}>{l}</button>)}
                  </div>
                </>
              ) : null}
            </div>
          )) : <div className="mtop"><span className="tt">{title}</span><span className="name" /></div>}
          <div className="mcontent">
            {view === 'list' ? <VentureList projects={projects} onOpen={openProj} onNew={() => setView('register')} onDelete={removeProject} onGroup={setGroup} onStar={setStar} lpByProject={lpByProject}
              {...(LOCKED_OFFICE ? { onLp: () => { setView('lp'); setPid(null); }, lpWait: lps.filter((l) => l.status === 'wait').length } : {})} /> : null}
            {view === 'lp' ? <LpPool rows={lps} projects={projects} loaded={lpLoaded} error={lpErr} onReload={loadLp} toast={setToast} onSave={saveLp} onDelete={removeLp} onPatchRow={patchLpRow} /> : null}
            {view === 'register' ? (
              <VentureRegister reg={reg} onField={regField} onCo={regCo} onCoPatch={patchCo} onCoPatches={patchCos} onAddCo={addCo} onRemoveCo={removeCo} onCancel={goList} onSubmit={submitReg} />
            ) : null}
            {view === 'detail' && cur ? <VentureDetail p={cur} tab={tab} onAdvance={advanceStep} onBackStep={backStep} onPatch={patchCur} toast={setToast}
              saveInfo={saving ? '저장 중…' : saveErr ? '⚠ 저장 실패' : (saveAt ? '✓ 자동저장 ' + saveAt : '')} saveError={saveErr} onRetrySave={retrySave} /> : null}
          </div>
        </div>
      </div>
      {toast ? <div className="toast show" onClick={() => setToast('')}>{toast}</div> : null}
    </div>
  );
}

function emptyProject(org: string): Project {
  return {
    id: 'tmp', org, addr: '', total: '', per: '1,000,000', dur: '5',
    gp: '', gpTel: '', gpEmail: '', regDate: todayISO(), fp: '', fpCo: '한화생명', cos: [blankCo()], step: 1,
  };
}

function normalize(data: unknown, id: string, org: string, step: number): Project {
  const d = (data || {}) as Project;
  const st = typeof d.step === 'number' && d.step >= 1 ? d.step : step || 1;
  return {
    ...emptyProject(org),
    ...d,
    id,
    org,
    step: Math.min(STEPS.length, Math.max(1, st)), // STEPS.length(15) 로 클램프 — 하드코딩 상한이 뒷단계를 11로 강등하던 결함 수정 · data.stepsVer(마이그레이션 108 표식)는 …d 로 보존
    done: !!d.done,
    cos: Array.isArray(d.cos) && d.cos.length ? d.cos : [blankCo()],
  };
}
